import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../hooks/use-session.js";
import { useOrgs, useCreateOrg } from "../hooks/use-orgs.js";
import { AppShell } from "../components/app-shell.js";
import { Button } from "../components/button.js";
import { FormField } from "../components/form-field.js";
import type { OrgType } from "../api/types.js";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ORG_TYPE_LABELS: Record<OrgType, string> = {
  public_body: "Public body / government",
  private_company: "Private company",
  non_profit: "Non-profit / charity",
  educational: "Educational institution",
  healthcare: "Healthcare provider",
  other: "Other",
};

const COUNTRY_OPTIONS = [
  { code: "AF", label: "Afghanistan" },
  { code: "AL", label: "Albania" },
  { code: "DZ", label: "Algeria" },
  { code: "AR", label: "Argentina" },
  { code: "AU", label: "Australia" },
  { code: "AT", label: "Austria" },
  { code: "BH", label: "Bahrain" },
  { code: "BD", label: "Bangladesh" },
  { code: "BE", label: "Belgium" },
  { code: "BR", label: "Brazil" },
  { code: "BG", label: "Bulgaria" },
  { code: "CA", label: "Canada" },
  { code: "CL", label: "Chile" },
  { code: "CN", label: "China" },
  { code: "CO", label: "Colombia" },
  { code: "HR", label: "Croatia" },
  { code: "CY", label: "Cyprus" },
  { code: "CZ", label: "Czech Republic" },
  { code: "DK", label: "Denmark" },
  { code: "EG", label: "Egypt" },
  { code: "EE", label: "Estonia" },
  { code: "FI", label: "Finland" },
  { code: "FR", label: "France" },
  { code: "DE", label: "Germany" },
  { code: "GR", label: "Greece" },
  { code: "HK", label: "Hong Kong" },
  { code: "HU", label: "Hungary" },
  { code: "IS", label: "Iceland" },
  { code: "IN", label: "India" },
  { code: "ID", label: "Indonesia" },
  { code: "IE", label: "Ireland" },
  { code: "IL", label: "Israel" },
  { code: "IT", label: "Italy" },
  { code: "JP", label: "Japan" },
  { code: "KE", label: "Kenya" },
  { code: "KR", label: "Korea, South" },
  { code: "KW", label: "Kuwait" },
  { code: "LV", label: "Latvia" },
  { code: "LT", label: "Lithuania" },
  { code: "LU", label: "Luxembourg" },
  { code: "MY", label: "Malaysia" },
  { code: "MT", label: "Malta" },
  { code: "MX", label: "Mexico" },
  { code: "NL", label: "Netherlands" },
  { code: "NZ", label: "New Zealand" },
  { code: "NG", label: "Nigeria" },
  { code: "NO", label: "Norway" },
  { code: "PK", label: "Pakistan" },
  { code: "PE", label: "Peru" },
  { code: "PH", label: "Philippines" },
  { code: "PL", label: "Poland" },
  { code: "PT", label: "Portugal" },
  { code: "QA", label: "Qatar" },
  { code: "RO", label: "Romania" },
  { code: "SA", label: "Saudi Arabia" },
  { code: "RS", label: "Serbia" },
  { code: "SG", label: "Singapore" },
  { code: "SK", label: "Slovakia" },
  { code: "SI", label: "Slovenia" },
  { code: "ZA", label: "South Africa" },
  { code: "ES", label: "Spain" },
  { code: "SE", label: "Sweden" },
  { code: "CH", label: "Switzerland" },
  { code: "TW", label: "Taiwan" },
  { code: "TH", label: "Thailand" },
  { code: "TR", label: "Turkey" },
  { code: "UA", label: "Ukraine" },
  { code: "AE", label: "United Arab Emirates" },
  { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" },
  { code: "VN", label: "Vietnam" },
];

export function OrgSetupPage() {
  const navigate = useNavigate();
  const { user } = useSession();
  const { data: orgsData, isLoading: orgsLoading } = useOrgs();
  const createOrg = useCreateOrg();

  const [orgName, setOrgName] = useState("");
  const [orgType, setOrgType] = useState<OrgType>("private_company");
  const [country, setCountry] = useState("GB");
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");
  const [emailError, setEmailError] = useState<string | null>(null);

  // Sync contactEmail when user data arrives after initial mount
  useEffect(() => {
    if (user?.email && !contactEmail) {
      setContactEmail(user.email);
    }
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const existingOrg = orgsData?.orgs[0];

  function handleEmailBlur() {
    const trimmed = contactEmail.trim();
    if (trimmed && !EMAIL_RE.test(trimmed)) {
      setEmailError("Enter a valid email address");
    } else {
      setEmailError(null);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const trimmed = contactEmail.trim();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setEmailError("Enter a valid email address");
      return;
    }

    try {
      const result = await createOrg.mutateAsync({
        orgName: orgName.trim(),
        orgType,
        country,
        accessibilityContactEmail: contactEmail.trim(),
      });
      void navigate("/setup/website", { state: { orgId: result.org.id } });
    } catch {
      // Error is displayed via createOrg.isError
    }
  }

  if (orgsLoading) {
    return (
      <AppShell>
        <div className="loading-text">Loading…</div>
      </AppShell>
    );
  }

  if (existingOrg) {
    return (
      <AppShell>
        <h1 className="page-title">Organisation already set up</h1>
        <p className="page-subtitle">
          <strong>{existingOrg.name}</strong> is already configured.
        </p>
        <Button onClick={() => void navigate("/setup/website")}>
          Continue to add website
        </Button>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <h1 className="page-title">Set up your organisation</h1>
      <p className="page-subtitle">
        Tell us about the organisation that owns the website.
      </p>

      {createOrg.error && (
        <div className="alert alert--error" role="alert">
          {createOrg.error.message}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate style={{ maxWidth: 520 }}>
        <FormField id="orgName" label="Organisation name" required>
          <input
            id="orgName"
            className="form-field__input"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="Acme Ltd"
            required
          />
        </FormField>

        <FormField
          id="orgType"
          label="Organisation type"
          required
          hint="This affects which accessibility regulations apply to you."
        >
          <select
            id="orgType"
            className="form-field__select"
            value={orgType}
            onChange={(e) => setOrgType(e.target.value as OrgType)}
          >
            {(Object.keys(ORG_TYPE_LABELS) as OrgType[]).map((t) => (
              <option key={t} value={t}>
                {ORG_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </FormField>

        <FormField id="country" label="Country" required>
          <select
            id="country"
            className="form-field__select"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          id="contactEmail"
          label="Accessibility contact email"
          required
          hint="The email published in your accessibility statement for users to report issues."
          error={emailError ?? undefined}
        >
          <input
            id="contactEmail"
            type="email"
            className="form-field__input"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            onBlur={handleEmailBlur}
            placeholder="accessibility@example.com"
            required
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? "contactEmail-error" : undefined}
          />
        </FormField>

        <Button
          type="submit"
          loading={createOrg.isPending}
          disabled={!orgName.trim() || !contactEmail.trim()}
        >
          Save and continue
        </Button>
      </form>
    </AppShell>
  );
}
